import React, { useState } from 'react'

interface PracticalScoreFormData {
  score: number
  notes: string
  attachment?: File
}

interface PracticalScoreFormProps {
  onSubmit: (data: PracticalScoreFormData) => void
  isLoading?: boolean
}

export const PracticalScoreForm: React.FC<PracticalScoreFormProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const [score, setScore] = useState('')
  const [notes, setNotes] = useState('')
  const [attachment, setAttachment] = useState<File | undefined>(undefined)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!score) {
      newErrors.score = 'Score is required'
    } else {
      const scoreNum = parseFloat(score)
      if (isNaN(scoreNum) || scoreNum < 0 || scoreNum > 100) {
        newErrors.score = 'Score must be between 0 and 100'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    onSubmit({
      score: parseFloat(score),
      notes,
      attachment,
    })

    // Clear form after successful submission
    setScore('')
    setNotes('')
    setAttachment(undefined)
    setErrors({})
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setAttachment(file)
  }

  return (
    <form onSubmit={handleSubmit} className="practical-score-form bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Submit Practical Score</h3>

      <div className="space-y-4">
        <div>
          <label htmlFor="score" className="block text-sm font-semibold text-gray-700 mb-2">
            Score (0-100)
          </label>
          <input
            id="score"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            disabled={isLoading}
            className={`w-full px-3 py-2 border-2 rounded focus:outline-none focus:border-blue-500 ${
              errors.score ? 'border-red-500' : 'border-gray-200'
            } ${isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
          {errors.score && <p className="text-red-600 text-sm mt-1">{errors.score}</p>}
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isLoading}
            rows={4}
            className={`w-full px-3 py-2 border-2 border-gray-200 rounded focus:outline-none focus:border-blue-500 ${
              isLoading ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
            placeholder="Add any comments or feedback..."
          />
        </div>

        <div>
          <label htmlFor="attachment" className="block text-sm font-semibold text-gray-700 mb-2">
            Attachment (Optional)
          </label>
          <input
            id="attachment"
            type="file"
            onChange={handleFileChange}
            disabled={isLoading}
            className={`w-full px-3 py-2 border-2 border-gray-200 rounded focus:outline-none focus:border-blue-500 ${
              isLoading ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
          />
          {attachment && (
            <p className="text-sm text-gray-600 mt-1">Selected: {attachment.name}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 rounded font-semibold transition-colors ${
            isLoading
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLoading ? 'Submitting...' : 'Submit Score'}
        </button>
      </div>
    </form>
  )
}
